import Links from '@/shared/constants/navegacao';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { DataAmigavel, DiasEntreDuasDatas } from '@/shared/utils/datas';
import { GetDefensorias } from '@/modules/structure/defensoria/defensoria.service';
import { GetNucleos } from '@/modules/structure/nucleo/nucleo.service';
import { EnumerateDefensores } from '@/modules/people/defensor.service';
import { readPerson } from '@/modules/people/person.service';
import { FindPersonFiltersDto } from '@/api.types.extra';
import { Link, useSearchParams } from 'react-router-dom';
import { LotacaoService } from '@/modules/business/lotacoes/lotacao.service';
import { ReadLotacaoDto, ReadDefensoriaDto, ReadNucleoDto, ReadDefensorResponseDto, ReadPersonDto } from '@/api.types';
import { SearchDtoLotacoes } from '@/shared/types/search.dto';
import { TipoLotacaoList, TipoLotacaoEstagioList } from '@/shared/constants/enumlist';
import { toast } from 'react-toastify';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useEffect, useState, useMemo, useCallback } from 'react';
import Select from 'react-select';

interface IOpcaoSelect {
  value: number;
  label: string;
}

// Constantes de parâmetros de busca
const SEARCH_PARAMS = {
  defensoriaid: 'defensoriaid',
  nucleoid: 'nucleoid',
  defensorid: 'defensorid',
  estagiarioid: 'estagiarioid',
  colaboradorid: 'colaboradorid',
  tipo: 'tipo',
} as const;

// Lista de tipos de lotação memoizada
const TIPO_LOTACAO_LIST = TipoLotacaoList.concat(TipoLotacaoEstagioList);

type TipoPessoa = 'defensor' | 'colaborador' | 'estagiario';

const TIPO_PESSOA_OPCOES: { value: TipoPessoa; label: string }[] = [
  { value: 'defensor', label: 'Defensores' },
  { value: 'colaborador', label: 'Colaboradores' },
  { value: 'estagiario', label: 'Estagiários' },
];

const TITULOS_POR_TIPO: Record<TipoPessoa, string> = {
  defensor: 'Vínculos de Defensores',
  colaborador: 'Vínculos de Colaboradores',
  estagiario: 'Vínculos de Estagiários',
};

export default function LotacoesIndexPage() {
  const { setActions } = useBreadcrumbActions();
  // Serviços - criado uma vez
  const lotacaoService = useMemo(() => new LotacaoService(), []);

  // Parâmetros de busca
  const [searchParams, setSearchParams] = useSearchParams();

  const [registros, setRegistros] = useState<ReadLotacaoDto[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const tableState = useDataTableState('data_inicial');

  // Filtros na tela
  const [defensorias, setDefensorias] = useState<ReadDefensoriaDto[]>([]);
  const [nucleos, setNucleos] = useState<ReadNucleoDto[]>([]);
  const [defensores, setDefensores] = useState<ReadDefensorResponseDto[]>([]);
  const [estagiarios, setEstagiarios] = useState<ReadPersonDto[]>([]);
  const [colaboradores, setColaboradores] = useState<ReadPersonDto[]>([]);
  const [defensoriaId, setDefensoriaId] = useState<number>(0);
  const [nucleoId, setNucleoId] = useState<number>(0);
  const [defensorId, setDefensorId] = useState<number>(0);
  const [estagiarioId, setEstagiarioId] = useState<number>(0);
  const [colaboradorId, setColaboradorId] = useState<number>(0);

  // Determina o tipo de pessoa baseado no parâmetro da URL (padrão: defensor)
  const tipoPessoa = useMemo((): TipoPessoa => {
    const tipo = searchParams.get(SEARCH_PARAMS.tipo);
    if (tipo === 'colaborador' || tipo === 'estagiario' || tipo === 'defensor') {
      return tipo;
    }
    return 'defensor';
  }, [searchParams]);

  // Determina qual botão de cadastrar mostrar baseado no tipo
  const cadastrarInfo = useMemo(() => {
    const links = {
      defensor: Links.Vinculos_Defensor_Criar,
      colaborador: Links.Vinculos_Colaboradores_Criar,
      estagiario: Links.Vinculos_Estagio_Criar,
    };
    const textos = {
      defensor: 'Cadastrar Vínculo para Defensor',
      colaborador: 'Cadastrar Vínculo para Colaborador',
      estagiario: 'Cadastrar Vínculo para Estagiário',
    };
    return {
      link: links[tipoPessoa],
      text: textos[tipoPessoa],
    };
  }, [tipoPessoa]);

  useEffect(() => {
    setActions({
      primary: {
        label: cadastrarInfo.text,
        href: cadastrarInfo.link,
        onClick: () => { },
      },
    });
    return () => setActions({});
  }, [setActions, cadastrarInfo.link, cadastrarInfo.text]);

  // Carrega listas de defensorias, núcleos e pessoas conforme o tipo
  useEffect(() => {
    Promise.all([
      GetDefensorias(0, 1000),
      GetNucleos(0, 1000),
    ])
      .then(([defensoriasResult, nucleosResult]) => {
        setDefensorias(defensoriasResult.objects);
        setNucleos(nucleosResult.objects);
      })
      .catch((res) => {
        toast.error(String(res) || 'Erro ao carregar defensorias e núcleos');
      });

    if (tipoPessoa === 'defensor') {
      EnumerateDefensores(0, 10000)
        .then((defensoresResult) => {
          setDefensores(defensoresResult.objects);
        })
        .catch((res) => {
          toast.error(String(res) || 'Erro ao carregar defensores');
        });
    } else if (tipoPessoa === 'estagiario') {
      const filters: FindPersonFiltersDto = {
        skip: 0,
        take: 10000,
        comEstagiario: true,
        isEstagiario: true,
        comEstagios: true,
      };
      readPerson(filters)
        .then((estagiariosResult) => {
          const estagiariosOrdenados = [...estagiariosResult[0]].sort((a, b) => {
            const nomeA = (a.nome || '').toLowerCase();
            const nomeB = (b.nome || '').toLowerCase();
            return nomeA.localeCompare(nomeB, 'pt-BR');
          });
          setEstagiarios(estagiariosOrdenados);
        })
        .catch((res) => {
          toast.error(String(res) || 'Erro ao carregar estagiários');
        });
    } else if (tipoPessoa === 'colaborador') {
      const filters: FindPersonFiltersDto = {
        skip: 0,
        take: 10000,
        comColaborador: true,
        isColaborador: true,
      };
      readPerson(filters)
        .then((colaboradoresResult) => {
          const colaboradoresOrdenados = [...colaboradoresResult[0]].sort((a, b) => {
            const nomeA = (a.nome || '').toLowerCase();
            const nomeB = (b.nome || '').toLowerCase();
            return nomeA.localeCompare(nomeB, 'pt-BR');
          });
          setColaboradores(colaboradoresOrdenados);
        })
        .catch((res) => {
          toast.error(String(res) || 'Erro ao carregar colaboradores');
        });
    }
  }, [tipoPessoa]);

  const handleTrocarTipo = useCallback(
    (novoTipo: TipoPessoa) => {
      if (novoTipo === tipoPessoa) return;

      setDefensorId(0);
      setEstagiarioId(0);
      setColaboradorId(0);
      tableState.setCurrentPage(1);

      const novosParams = new URLSearchParams(searchParams);
      novosParams.set(SEARCH_PARAMS.tipo, novoTipo);
      novosParams.delete(SEARCH_PARAMS.defensorid);
      novosParams.delete(SEARCH_PARAMS.estagiarioid);
      novosParams.delete(SEARCH_PARAMS.colaboradorid);
      setSearchParams(novosParams);
    },
    [searchParams, setSearchParams, tableState, tipoPessoa],
  );

  // Inicializa filtros a partir dos searchParams
  useEffect(() => {
    if (searchParams.has(SEARCH_PARAMS.defensoriaid)) {
      setDefensoriaId(Number(searchParams.get(SEARCH_PARAMS.defensoriaid)));
    }
    if (searchParams.has(SEARCH_PARAMS.nucleoid)) {
      setNucleoId(Number(searchParams.get(SEARCH_PARAMS.nucleoid)));
    }
    if (searchParams.has(SEARCH_PARAMS.defensorid)) {
      setDefensorId(Number(searchParams.get(SEARCH_PARAMS.defensorid)));
    }
    if (searchParams.has(SEARCH_PARAMS.estagiarioid)) {
      setEstagiarioId(Number(searchParams.get(SEARCH_PARAMS.estagiarioid)));
    }
    if (searchParams.has(SEARCH_PARAMS.colaboradorid)) {
      setColaboradorId(Number(searchParams.get(SEARCH_PARAMS.colaboradorid)));
    }
  }, [searchParams]);

  useEffect(() => {
    handleBuscar();
  }, [tableState.currentPage, tableState.pageSize, tableState.sortConfig, tableState.filter, defensoriaId, nucleoId, defensorId, estagiarioId, colaboradorId, tipoPessoa]);

  const onFilter = useCallback((condicoes: SearchDtoLotacoes) => {
    lotacaoService
      .Enumerate(condicoes)
      .then((result) => {
        setRegistros(result[0]);
        setTotalRecords(result[1]);
      })
      .catch((res) => {
        toast.error(String(res) || 'Erro ao buscar vínculos');
      });
  }, [lotacaoService]);

  const handleBuscar = useCallback(() => {
    const s: SearchDtoLotacoes = {
      skip: (tableState.currentPage - 1) * tableState.pageSize,
      take: tableState.pageSize,
      tipo_pessoa: tipoPessoa,
    } as SearchDtoLotacoes;

    if (defensoriaId > 0) {
      s.id_defensoria = defensoriaId;
    }

    if (nucleoId > 0) {
      s.id_nucleo = nucleoId;
    }

    if (defensorId > 0 && tipoPessoa === 'defensor') {
      s.id_defensor = defensorId;
    }

    if (estagiarioId > 0 && tipoPessoa === 'estagiario') {
      s.id_estagiario = estagiarioId;
    }

    if (colaboradorId > 0 && tipoPessoa === 'colaborador') {
      s.id_colaborador = colaboradorId;
    }

    onFilter(s);
  }, [tableState.currentPage, tableState.pageSize, tipoPessoa, defensoriaId, nucleoId, defensorId, estagiarioId, colaboradorId, onFilter]);

  const colunas: Column<ReadLotacaoDto>[] = useMemo(() => [
    {
      key: 'tipo',
      header: 'Tipo',
      render: (row) => {
        return TIPO_LOTACAO_LIST.find((a) => a.value === row.tipo)?.name || '';
      },
    },
    {
      key: 'local',
      header: 'Local',
      render: (row) => {
        return row.defensoria?.nome || row.nucleo?.nome || '';
      },
    },
    {
      key: 'setor.nome',
      header: 'Setor',
      render: (row) => row.setor?.nome || '',
    },
    {
      key: 'pessoa',
      header: 'Pessoa',
      render: (row) => {
        const nome = row.defensor?.pessoa?.nome || row.colaborador?.pessoa?.nome || row.estagio?.estagiario?.pessoa?.nome || '';
        const ativo = row.defensor?.ativo !== undefined
          ? row.defensor.ativo
          : row.estagio?.estagiario?.ativo !== undefined
            ? row.estagio.estagiario.ativo
            : true;
        return (
          <>
            {nome}
            {!ativo && <span className="text-muted"> (inativo)</span>}
          </>
        );
      },
    },
    {
      key: 'data_inicial',
      header: 'Início',
      type: 'date',
      render: (row) => DataAmigavel(row.data_inicial),
    },
    {
      key: 'data_final',
      header: 'Fim',
      type: 'date',
      render: (row) => (
        <span
          title={
            DiasEntreDuasDatas(row.data_inicial, row.data_final).toString() +
            ' dias'
          }
        >
          {DataAmigavel(row.data_final)}
        </span>
      ),
    },
    {
      key: 'acoes',
      header: 'Ações',
      render: (row) => (
        <Link className="link-secondary" to={`${Links.Vinculos_Editar}/${row.id}`}>
          Editar
        </Link>
      ),
    },
  ], []);

  // Opções para react-select - Defensores
  const opcoesDefensores = useMemo<IOpcaoSelect[]>(() => {
    const opcoes = defensores.map((item) => ({
      value: item.id,
      label: item.pessoa?.nome || '',
    }));
    return [{ value: 0, label: '(todos os defensores)' }, ...opcoes];
  }, [defensores]);

  // Opções para react-select - Estagiários
  const opcoesEstagiarios = useMemo<IOpcaoSelect[]>(() => {
    const opcoes = estagiarios
      .filter((item) => item.estagiario?.id)
      .map((item) => ({
        value: item.estagiario!.id,
        label: item.nome || '',
      }));
    return [{ value: 0, label: '(todos os estagiários)' }, ...opcoes];
  }, [estagiarios]);

  // Opções para react-select - Colaboradores
  const opcoesColaboradores = useMemo<IOpcaoSelect[]>(() => {
    const opcoes = colaboradores
      .filter((item) => item.colaborador?.id)
      .map((item) => ({
        value: item.colaborador!.id,
        label: item.nome || '',
      }));
    return [{ value: 0, label: '(todos os colaboradores)' }, ...opcoes];
  }, [colaboradores]);

  // Opções para react-select - Defensorias
  const opcoesDefensorias = useMemo<IOpcaoSelect[]>(() => {
    const opcoes = defensorias.map((item) => ({
      value: item.id,
      label: item.nome || '',
    }));
    return [{ value: 0, label: '(todas as defensorias)' }, ...opcoes];
  }, [defensorias]);

  // Opções para react-select - Núcleos
  const opcoesNucleos = useMemo<IOpcaoSelect[]>(() => {
    const opcoes = nucleos.map((item) => ({
      value: item.id,
      label: item.nome || '',
    }));
    return [{ value: 0, label: '(todos os núcleos)' }, ...opcoes];
  }, [nucleos]);

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2">
            <h3 className="mb-0">{TITULOS_POR_TIPO[tipoPessoa]}</h3>
            <div className="btn-group" role="group" aria-label="Tipo de vínculo">
              {TIPO_PESSOA_OPCOES.map((opcao) => (
                <button
                  key={opcao.value}
                  type="button"
                  className={`btn ${tipoPessoa === opcao.value ? 'btn-success' : 'btn-outline-success'}`}
                  aria-pressed={tipoPessoa === opcao.value}
                  onClick={() => handleTrocarTipo(opcao.value)}
                >
                  {opcao.label}
                </button>
              ))}
            </div>
          </div>
          <span className="blockquote-footer pb-4 text-end">
            Vínculos em Defensorias e Núcleos.
          </span>
        </div>

        <hr className="pb-4" />

        {registros && (
          <DataTable
            columns={colunas}
            data={registros}
            onChange={handleBuscar}
            serverSide={true}
            states={tableState}
            totalRecords={totalRecords}
            showFilter={false}
            renderFilters={() => (
              <div className='col-md-8'>
                <div className='row'>
                  {tipoPessoa === 'defensor' && (
                    <div className="col-md-4">
                      <Select
                        id="defensor"
                        value={opcoesDefensores.find((op) => op.value === defensorId)}
                        options={opcoesDefensores}
                        placeholder="Selecione um defensor..."
                        isSearchable={true}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        onChange={(opcao) => {
                          const novoId = opcao ? opcao.value : 0;
                          setDefensorId(novoId);
                          tableState.setCurrentPage(1);
                        }}
                      />
                    </div>
                  )}
                  {tipoPessoa === 'estagiario' && (
                    <div className="col-md-4">
                      <Select
                        id="estagiario"
                        value={opcoesEstagiarios.find((op) => op.value === estagiarioId)}
                        options={opcoesEstagiarios}
                        placeholder="Selecione um estagiário..."
                        isSearchable={true}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        onChange={(opcao) => {
                          const novoId = opcao ? opcao.value : 0;
                          setEstagiarioId(novoId);
                          tableState.setCurrentPage(1);
                        }}
                      />
                    </div>
                  )}
                  {tipoPessoa === 'colaborador' && (
                    <div className="col-md-4">
                      <Select
                        id="colaborador"
                        value={opcoesColaboradores.find((op) => op.value === colaboradorId)}
                        options={opcoesColaboradores}
                        placeholder="Selecione um colaborador..."
                        isSearchable={true}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        onChange={(opcao) => {
                          const novoId = opcao ? opcao.value : 0;
                          setColaboradorId(novoId);
                          tableState.setCurrentPage(1);
                        }}
                      />
                    </div>
                  )}
                  <div className={tipoPessoa === 'defensor' || tipoPessoa === 'estagiario' || tipoPessoa === 'colaborador' ? "col-md-4" : "col-md-5"}>
                    <Select
                      id="defensoria"
                      value={opcoesDefensorias.find((op) => op.value === defensoriaId)}
                      options={opcoesDefensorias}
                      placeholder="Selecione uma defensoria..."
                      isSearchable={true}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      onChange={(opcao) => {
                        const novoId = opcao ? opcao.value : 0;
                        setDefensoriaId(novoId);
                        tableState.setCurrentPage(1);
                      }}
                    />
                  </div>

                  <div className={tipoPessoa === 'defensor' || tipoPessoa === 'estagiario' || tipoPessoa === 'colaborador' ? "col-md-4" : "col-md-5"}>
                    <Select
                      id="nucleo"
                      value={opcoesNucleos.find((op) => op.value === nucleoId)}
                      options={opcoesNucleos}
                      placeholder="Selecione um núcleo..."
                      isSearchable={true}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      onChange={(opcao) => {
                        const novoId = opcao ? opcao.value : 0;
                        setNucleoId(novoId);
                        tableState.setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>
              </div>

            )}
          />
        )}
      </div>
    </>
  );
}
